import { createFileRoute, Link } from '@tanstack/react-router';
import { useRef, useState, useEffect } from 'react';
import {
  useSupportConversation,
  useReplySupport,
  useSetSupportStatus,
  useSetSupportSpam,
} from '@/features/support/api';
import { SandboxedHtml } from '@/components/SandboxedHtml';
import { Button } from '@/components/ui/Button';
import type { SupportConversationStatus, SupportMessageDto } from '@honeycomb/shared';

// ── Status chip styling (mirrors support inbox) ───────────────────────────────

function statusChipStyle(
  status: SupportConversationStatus,
  isSpam: boolean,
): React.CSSProperties {
  if (isSpam) {
    return { backgroundColor: '#000000', color: '#ffffff' };
  }
  switch (status) {
    case 'needs_reply':
      return { backgroundColor: '#FF746C', color: '#000' };
    case 'waiting_on_customer':
      return { backgroundColor: '#B3EBF2', color: '#000' };
    case 'closed':
      return { backgroundColor: '#80EF80', color: '#000' };
    default:
      return { backgroundColor: '#FF746C', color: '#000' };
  }
}

export const Route = createFileRoute('/_authed/support/$id')({
  component: SupportDetailPage,
});

function statusLabel(status: SupportConversationStatus, isSpam: boolean): string {
  if (isSpam) return 'spam';
  return status.replace(/_/g, ' ');
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Render plain text body preserving newlines as <br>
function PlainTextBody({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <p className="text-sm text-ink whitespace-pre-wrap break-words">
      {lines.map((line, i) => (
        <span key={i}>
          {line}
          {i < lines.length - 1 && <br />}
        </span>
      ))}
    </p>
  );
}

function MessageRow({ msg }: { msg: SupportMessageDto }) {
  const isOutbound = msg.direction === 'outbound';
  return (
    <div
      data-testid={`support-message-${msg.id}`}
      data-direction={msg.direction}
      className={[
        'rounded-xl border px-4 py-3',
        isOutbound ? 'bg-honey-tint border-honey/30' : 'bg-paper',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        maxWidth: '500px',
        marginLeft: isOutbound ? 'auto' : 0,
        marginRight: isOutbound ? 0 : 'auto',
        borderColor: isOutbound ? undefined : 'var(--color-hairline)',
      }}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-xs font-medium text-ink-soft">
          {msg.fromName ?? msg.fromEmail}
          {isOutbound && ' (you)'}
        </span>
        <span className="text-xs text-ink-faint tabular-nums">
          {formatDateTime(msg.createdAt)}
        </span>
      </div>

      {!isOutbound && msg.bodyHtml ? (
        <SandboxedHtml
          html={msg.bodyHtml}
          testId={`support-message-iframe-${msg.id}`}
        />
      ) : (
        <PlainTextBody text={msg.bodyText} />
      )}
    </div>
  );
}

function SupportDetailPage() {
  const { id } = Route.useParams();
  const { data, isLoading } = useSupportConversation(id);
  const conv = data?.conversation;

  const reply = useReplySupport(id);
  const setStatus = useSetSupportStatus(id);
  const setSpam = useSetSupportSpam(id);

  const [replyText, setReplyText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const wasReplyPending = useRef(false);

  // Focus textarea only after a submit completes (pending true → false transition).
  useEffect(() => {
    if (wasReplyPending.current && !reply.isPending) {
      textareaRef.current?.focus();
    }
    wasReplyPending.current = reply.isPending;
  }, [reply.isPending]);

  const handleReplySubmit = () => {
    const body = replyText.trim();
    if (!body) {
      textareaRef.current?.focus();
      return;
    }
    // Send with original value (newlines preserved)
    reply.mutate(
      { body: replyText },
      {
        onSuccess: () => {
          setReplyText('');
        },
      },
    );
  };

  const handleSetStatus = (status: SupportConversationStatus) => {
    setStatus.mutate(status);
  };

  const handleToggleSpam = () => {
    setSpam.mutate(!(conv?.isSpam ?? false));
  };

  if (isLoading) {
    return (
      <div data-testid="support-detail" className="p-6 text-ink-soft">
        Loading…
      </div>
    );
  }

  if (!conv) {
    return (
      <div data-testid="support-detail" className="p-6 text-ink-soft">
        Conversation not found.
      </div>
    );
  }

  const ctx = conv.accountContext;

  return (
    <div data-testid="support-detail" className="p-4 sm:p-6 lg:p-8">
      {/* Back link — only visible at narrow viewport (<md) */}
      <Link
        to="/support"
        data-testid="support-back"
        className="md:hidden inline-flex items-center gap-1 text-sm text-honey mb-4 hover:underline"
      >
        ← Back to inbox
      </Link>

      {/* Header */}
      <header className="mb-4">
        <h1 className="font-serif text-2xl leading-tight break-words">
          {conv.subject}
        </h1>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {/* Status pill */}
          <span
            data-testid="support-status-pill"
            className="inline-block rounded-full px-3 py-1 text-xs font-medium"
            style={statusChipStyle(conv.status, conv.isSpam)}
          >
            {statusLabel(conv.status, conv.isSpam)}
          </span>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Main column — messages + reply */}
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          {/* Message thread */}
          <section>
            <div
              data-testid="support-message-list"
              className="flex flex-col gap-3"
            >
              {conv.messages.map((msg) => (
                <MessageRow key={msg.id} msg={msg} />
              ))}
            </div>
          </section>

          {/* Reply composer */}
          <section className="rounded-xl border bg-paper p-4" style={{ borderColor: 'var(--color-hairline)' }}>
            <div className="text-xs font-medium text-ink-soft mb-2 uppercase tracking-wide">
              Reply
            </div>
            <textarea
              ref={textareaRef}
              data-testid="support-reply-body"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={5}
              placeholder="Write your reply…"
              className="w-full rounded-lg border px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-honey/40"
              style={{ borderColor: 'var(--color-hairline)' }}
              disabled={reply.isPending}
            />
            <div className="flex justify-end mt-2">
              <Button
                data-testid="support-reply-submit"
                onClick={handleReplySubmit}
                disabled={reply.isPending}
              >
                {reply.isPending ? 'Sending…' : 'Send reply'}
              </Button>
            </div>
            {reply.isError && (
              <p className="text-xs mt-2" style={{ color: 'var(--color-error-text)' }}>
                Failed to send reply. Please try again.
              </p>
            )}
          </section>
        </div>

        {/* Sidebar */}
        <aside className="lg:w-64 flex flex-col gap-4 flex-none">
          {/* Account context */}
          <div className="rounded-xl border bg-paper p-4" style={{ borderColor: 'var(--color-hairline)' }}>
            <div className="text-xs font-medium text-ink-soft uppercase tracking-wide mb-3">
              Sender
            </div>
            {ctx ? (
              <div
                data-testid="support-account-context"
                data-user-id={ctx.userId}
                data-display-name={ctx.displayName}
                data-plan={ctx.plan}
                className="flex flex-col gap-1"
              >
                <div className="font-medium text-sm">{ctx.displayName}</div>
                <div className="text-xs text-ink-soft">{ctx.email}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs rounded-full px-2 py-0.5 bg-cream text-ink-soft capitalize">
                    {ctx.plan}
                  </span>
                </div>
                <div className="text-xs text-ink-faint mt-1">
                  Joined {new Date(ctx.signupAt).toLocaleDateString([], { month: 'short', year: 'numeric' })}
                </div>
                {conv.userId != null && (
                  <a
                    href={`/users/${conv.userId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="support-detail-view-user"
                    className="text-xs text-honey hover:underline mt-1 inline-block"
                  >
                    View user
                  </a>
                )}
              </div>
            ) : (
              <div data-testid="support-account-empty" className="text-sm text-ink-soft">
                No linked account
              </div>
            )}
          </div>

          {/* Sent to */}
          {conv.receivedToAddress != null && (
            <div
              data-testid="support-detail-sent-to"
              className="rounded-xl border bg-paper p-4"
              style={{ borderColor: 'var(--color-hairline)' }}
            >
              <div className="text-xs font-medium text-ink-soft uppercase tracking-wide mb-3">
                Sent to
              </div>
              <div
                data-testid="support-detail-sent-to-value"
                className="text-sm text-ink break-all"
              >
                {conv.receivedToAddress}
              </div>
            </div>
          )}

          {/* Status actions */}
          <div className="rounded-xl border bg-paper p-4 flex flex-col gap-2" style={{ borderColor: 'var(--color-hairline)' }}>
            <div className="text-xs font-medium text-ink-soft uppercase tracking-wide mb-1">
              Status
            </div>
            <Button
              variant="ghost"
              size="sm"
              data-testid="support-set-status-needs-reply"
              onClick={() => handleSetStatus('needs_reply')}
              disabled={setStatus.isPending}
              className="w-full justify-start"
            >
              Needs reply
            </Button>
            <Button
              variant="ghost"
              size="sm"
              data-testid="support-set-status-waiting"
              onClick={() => handleSetStatus('waiting_on_customer')}
              disabled={setStatus.isPending}
              className="w-full justify-start"
            >
              Waiting on customer
            </Button>
            <Button
              variant="ghost"
              size="sm"
              data-testid="support-set-status-closed"
              onClick={() => handleSetStatus('closed')}
              disabled={setStatus.isPending}
              className="w-full justify-start"
            >
              Closed
            </Button>
          </div>

          {/* Spam */}
          <div className="rounded-xl border bg-paper p-4" style={{ borderColor: 'var(--color-hairline)' }}>
            <Button
              variant="ghost"
              size="sm"
              data-testid="support-toggle-spam"
              onClick={handleToggleSpam}
              disabled={setSpam.isPending}
              className="w-full justify-start"
            >
              {conv.isSpam ? 'Not spam' : 'Mark as spam'}
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
}
